import * as React from 'react';
import { connect } from 'react-redux';
import { Languages } from 'common/types';
import { darken, Theme, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { ReduxState } from 'services/types';
import classNames from 'classnames';
import StripePattern from 'common/components/StripePattern';
import { BaseProductVariant } from 'common/modules/inventory';
import { getVariantName } from 'common/utils/productUtils';
import { getVariantValueNames } from 'common/modules/products/variants';

interface StateToProps {
	lang: Languages;
}

interface OwnProps {
	variant: BaseProductVariant;
	onAddVariantClick: (id: string) => void;
	availability: number | null | undefined;
	quantity: number | null | undefined;
	isSetWithVariants: boolean;
}

type Props = StateToProps & OwnProps & WithStyles<typeof styles>;

const VariantCard = (props: Props) => {
	const { variant, classes, availability, quantity, lang } = props;
	const isZeroOrLess = ((availability || availability === 0) && availability <= 0) ?? false;
	const isOverbooked = ((availability || availability === 0) && availability < 0) ?? false;
	const variantName = getVariantName(getVariantValueNames(variant), lang);

	const handleVariantCardClick = () => {
		props.onAddVariantClick(variant.id);
	};
	return (
		<div
			className={classNames(classes.addButton, { [classes.plain]: isZeroOrLess })}
			onClick={() => handleVariantCardClick()}
		>
			{isOverbooked && (
				<StripePattern
					color="dark"
					opacity={0.2}
					degrees={135}
					stripeWidth={2}
					nonStripeColorOpacity={0.0}
				/>
			)}
			<Typography align="center">{variantName}</Typography>
			{!props.isSetWithVariants && (availability === null || quantity === null) ? (
				<Typography className={classes.availability} variant="body2">
					∞
				</Typography>
			) : !props.isSetWithVariants && availability !== undefined && quantity !== undefined ? (
				<Typography className={classes.availability} variant="body2">
					{availability + ' / ' + quantity}
				</Typography>
			) : (
				<div />
			)}
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		addButton: {
			padding: '12px 24px',
			flex: '0 0 auto',
			margin: 8,
			minWidth: 48,
			backgroundColor: theme.palette.colors.availabilityGood.main,
			border: `1px solid ${theme.palette.colors.availabilityGood.dark}`,
			textTransform: 'none',
			maxWidth: 120,
			borderRadius: '4px',
			'&:hover': {
				backgroundColor: darken(theme.palette.colors.availabilityGood.main, 0.1),
				cursor: 'pointer',
			},
			justifyContent: 'center',
			position: 'relative',
		},
		plain: {
			backgroundColor: theme.palette.background.default,
			borderColor: theme.palette.border.inputOutline,
			'&:hover': {
				backgroundColor: theme.palette.background.default,
				cursor: 'pointer',
			},
		},
		availability: {
			marginTop: theme.spacing(0.5),
			textAlign: 'center',
		},
	});

const mapStateToProps = ({ lang }: ReduxState): StateToProps => {
	return {
		lang,
	};
};

export default withStyles(styles)(connect(mapStateToProps, {})(VariantCard));
